import {
  createAsyncThunk,
  createSelector,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import type {
  ThreadDetail,
  ThreadMessage,
  ThreadWorkflowRunSummary,
  SubThreadSummary,
  ThreadBlock,
  ThreadBlockChild,
} from "skedyul-ui/ui";
import { generateDBId } from "skedyul-db/ids/generate";
import { trpc } from "@utils/api";
import type { RootState, AppDispatch } from "../../index";
import {
  consumeThreadTitleStream,
  mapGetToThreadDetail,
  mapListItems,
  mapSendPayloadToMessage,
  type ThreadListRow,
} from "./threadMappers";
import { mergeBlocksFromServer } from "./threadRealtimeApply";
import { resolveParticipantsAfterServerMerge } from "./mergeCurrentFromServerParticipants";
import {
  isThreadInConversations,
  isThreadInInbox,
} from "skedyul-ui/src/components/ui/ThreadsSidebar/threadFilters";
import { INITIAL_MESSAGE_LIMIT, MESSAGE_PAGE_SIZE } from "@/constants/threads";

function updateThreadTitleInLists(
  state: ThreadsState,
  threadId: string,
  updates: { title?: string | null; listIcon?: string | null },
): void {
  const patchRow = (row: ThreadListRow): ThreadListRow =>
    row.id === threadId
      ? {
          ...row,
          ...(updates.title !== undefined ? { title: updates.title } : {}),
          ...(updates.listIcon !== undefined
            ? { listIcon: updates.listIcon }
            : {}),
        }
      : row;

  state.items = state.items.map(patchRow);
  state.exclusiveFeedItems = state.exclusiveFeedItems.map(patchRow);
  state.campaignItems = state.campaignItems.map(patchRow);
}

function mapPersistedSendParticipants(
  participants: Array<{
    id: string;
    displayName: string;
    avatarUrl: string | null;
    kind: string;
    role?: string | null;
    joinedAt: string;
    memberId?: string | null;
    agentId?: string | null;
    contactId?: string | null;
    workflowId?: string | null;
  }>,
): ThreadDetail["participants"] {
  return participants.map((participant) => ({
    id: participant.id,
    displayName: participant.displayName,
    avatarUrl: participant.avatarUrl,
    kind: participant.kind as ThreadDetail["participants"][number]["kind"],
    role: (participant.role ??
      "PARTICIPANT") as ThreadDetail["participants"][number]["role"],
    joinedAt: participant.joinedAt,
    memberId: participant.memberId ?? null,
    agentId: participant.agentId ?? null,
    contactId: participant.contactId ?? null,
    workflowId: participant.workflowId ?? null,
  }));
}

function syncListParticipantsFromDetail(
  items: ThreadListRow[],
  detail: ThreadDetail,
): void {
  const thread = items.find((item) => item.id === detail.id);
  if (!thread) return;

  thread.participants = detail.participants.map((participant) => ({
    id: participant.id,
    displayName: participant.displayName,
    avatarUrl: participant.avatarUrl,
    kind: participant.kind as ThreadListRow["participants"][number]["kind"],
    role: participant.role as ThreadListRow["participants"][number]["role"],
    joinedAt: participant.joinedAt,
    memberId: participant.memberId ?? null,
    contactId: participant.contactId ?? null,
    agentId: participant.agentId ?? null,
    workflowId: participant.workflowId ?? null,
  }));
}

export const NEW_THREAD_SLUG = "new";

export type SenderSnapshot = {
  memberId: string;
  displayName: string;
  avatarUrl: string | null;
};

export type AgentToolCall = {
  id: string;
  name: string;
  description?: string;
  args?: Record<string, unknown>;
  status: "pending" | "pending_approval" | "success" | "error";
  result?: string;
  error?: string;
  durationMs?: number;
};

export type AgentThought = {
  content: string;
  durationMs?: number;
  isThinking?: boolean;
};

export type ThreadAgentState = {
  isProcessing: boolean;
  agentRunId: string | null;
  agentId: string | null;
  agentName: string | null;
  agentKind: string | null;
  thought: AgentThought | null;
  toolCalls: AgentToolCall[];
};

export type NewThreadType = "internal" | "external";

export type ThreadSidebarFilterType = "inbox" | "conversations";
export type ThreadSidebarContactSort = "last_contact" | "last_activity";

export interface ThreadsState {
  items: ThreadListRow[];
  listLoading: boolean;
  listError: string | null;
  current: ThreadDetail | null;
  detailLoading: boolean;
  sendingMessage: boolean;
  titleStreamActive: boolean;
  titleStreamText: string;
  titleStreamThreadId: string | null;
  titleStreamRequestId: number;
  senderSnapshot: SenderSnapshot | null;
  senderSnapshotLoaded: boolean;
  agentState: Record<string, ThreadAgentState>;
  // New thread routing state
  newThreadType: NewThreadType;
  selectedChannelId: string | null;
  routingAddress: string;
  // Pending message for existing thread navigation
  pendingMessageDraft: { threadId: string; content: string } | null;
  // Message pagination state (bidirectional)
  hasMoreBefore: boolean;
  hasMoreAfter: boolean;
  beforeCursor: string | null;
  afterCursor: string | null;
  loadingOlderMessages: boolean;
  loadingNewerMessages: boolean;
  // Thread sidebar state
  sidebarFilter: ThreadSidebarFilterType;
  sidebarSearch: string;
  sidebarContactSort: ThreadSidebarContactSort;
  sidebarChannelId: string | null;
  // Stable list order state
  listLoadedAt: string | null;
  pendingNewThreads: string[];
  listNextCursor: string | null;
  listHasMore: boolean;
  listLoadingMore: boolean;
  /** Total distinct contacts with conversation threads (server count) */
  conversationContactCount: number | null;
  /** Exclusive feed threads pinned above pagination (empty while searching) */
  exclusiveFeedItems: ThreadListRow[];
  /** Campaign hubs shown under Pending and Scheduled for one week */
  campaignItems: ThreadListRow[];
  /**
   * Recipient pages for an expanded campaign group. Absent until that group
   * is opened, so a collapsed campaign does not paginate its chats.
   */
  campaignParticipantPages: Record<string, CampaignParticipantPage>;
}

type CampaignParticipantPage = {
  items: ThreadListRow[];
  nextCursor: string | null;
  hasMore: boolean;
  loading: boolean;
};

const initialState: ThreadsState = {
  items: [],
  listLoading: false,
  listError: null,
  current: null,
  detailLoading: false,
  sendingMessage: false,
  titleStreamActive: false,
  titleStreamText: "",
  titleStreamThreadId: null,
  titleStreamRequestId: 0,
  senderSnapshot: null,
  senderSnapshotLoaded: false,
  agentState: {},
  // New thread routing state
  newThreadType: "internal",
  selectedChannelId: null,
  routingAddress: "",
  // Pending message for existing thread navigation
  pendingMessageDraft: null,
  // Message pagination state (bidirectional)
  hasMoreBefore: false,
  hasMoreAfter: false,
  beforeCursor: null,
  afterCursor: null,
  loadingOlderMessages: false,
  loadingNewerMessages: false,
  // Thread sidebar state
  sidebarFilter: "inbox",
  sidebarSearch: "",
  sidebarContactSort: "last_activity",
  sidebarChannelId: null,
  // Stable list order state
  listLoadedAt: null,
  pendingNewThreads: [],
  listNextCursor: null,
  listHasMore: false,
  listLoadingMore: false,
  conversationContactCount: null,
  exclusiveFeedItems: [],
  campaignItems: [],
  campaignParticipantPages: {},
};

type TraceEvent = {
  index: number;
  kind: string;
  message?: {
    role: string;
    content: string;
    isFinal?: boolean;
  };
  toolCall?: {
    name: string;
    status?: string;
    args?: Record<string, unknown>;
    resultSummary?: string;
    error?: string;
    durationMs?: number;
    startedAt?: number;
  };
};

function buildAgentStateFromTrace(
  agentRunId: string,
  agentId: string,
  agentName: string,
  events: TraceEvent[] | undefined,
): ThreadAgentState {
  if (!events || events.length === 0) {
    return {
      isProcessing: true,
      agentRunId,
      agentId,
      agentName,
      agentKind: null,
      thought: null,
      toolCalls: [],
    };
  }

  const toolCallsMap = new Map<string, AgentToolCall>();
  const toolCallStartTimes = new Map<string, number>();
  let latestThought: AgentThought | null = null;
  let toolCallCounter = 0;

  for (const event of events) {
    if (event.kind === "THOUGHT" && event.message) {
      latestThought = {
        content: event.message.content,
        isThinking: !event.message.isFinal,
      };
    } else if (event.kind === "TOOL_CALL_STARTED" && event.toolCall) {
      const toolId = `${agentRunId}-${event.index}`;
      const key = event.toolCall.name + "-" + toolCallCounter;

      if (event.toolCall.startedAt) {
        toolCallStartTimes.set(key, event.toolCall.startedAt);
      }

      toolCallsMap.set(key, {
        id: toolId,
        name: event.toolCall.name,
        args: event.toolCall.args,
        status: "pending",
      });
      toolCallCounter++;
    } else if (event.kind === "TOOL_CALL_COMPLETED" && event.toolCall) {
      const toolName = event.toolCall.name;
      for (const [key, tc] of toolCallsMap.entries()) {
        if (key.startsWith(toolName + "-") && tc.status === "pending") {
          toolCallsMap.set(key, {
            ...tc,
            status: event.toolCall.status === "SUCCEEDED" ? "success" : "error",
            result: event.toolCall.resultSummary,
            error: event.toolCall.error,
            durationMs: event.toolCall.durationMs,
          });
          break;
        }
      }
    }
  }

  return {
    isProcessing: true,
    agentRunId,
    agentId,
    agentName,
    agentKind: null,
    thought: latestThought,
    toolCalls: Array.from(toolCallsMap.values()),
  };
}

const threadsSlice = createSlice({
  name: "threads",
  initialState,
  reducers: {
    applyRouteNavigation(
      state,
      action: PayloadAction<{
        next: string | null;
        prev: string | null | undefined;
      }>,
    ) {
      const { next, prev } = action.payload;
      if (!next || next === NEW_THREAD_SLUG) {
        state.titleStreamActive = false;
        state.titleStreamText = "";
        state.titleStreamThreadId = null;
        return;
      }
      const navigatedFromDraftToReal =
        prev === NEW_THREAD_SLUG && next !== NEW_THREAD_SLUG;
      if (navigatedFromDraftToReal) {
        return;
      }
      if (
        prev !== undefined &&
        prev !== null &&
        prev !== NEW_THREAD_SLUG &&
        prev !== next
      ) {
        state.titleStreamActive = false;
        state.titleStreamText = "";
        state.titleStreamThreadId = null;
      }
    },
    clearCurrentThread(state) {
      state.current = null;
      state.detailLoading = false;
    },
    setDetailLoading(state, action: PayloadAction<boolean>) {
      state.detailLoading = action.payload;
    },
    setCurrent(state, action: PayloadAction<ThreadDetail | null>) {
      state.current = action.payload;

      if (action.payload) {
        syncListParticipantsFromDetail(state.items, action.payload);
      }

      // Agent state is now managed via Pusher events (thread.agent.started, thread.agent.completed)
      // See agentStarted and agentCompleted reducers
    },
    mergeCurrentParticipants(
      state,
      action: PayloadAction<{
        threadId: string;
        participants: ThreadDetail["participants"];
      }>,
    ) {
      if (!state.current || state.current.id !== action.payload.threadId) {
        return;
      }
      if (action.payload.participants.length === 0) {
        return;
      }
      state.current.participants = action.payload.participants;
      syncListParticipantsFromDetail(state.items, state.current);
    },
    mergeCurrentFromServer(state, action: PayloadAction<ThreadDetail>) {
      const mapped = action.payload;
      if (!state.current || state.current.id !== mapped.id) {
        state.current = mapped;
        syncListParticipantsFromDetail(state.items, mapped);
        return;
      }
      const serverTitle = mapped.title?.trim();
      const prevMessages = state.current.messages;

      // Preserve optimistic messages that haven't been confirmed by the server yet.
      // These are messages with outboundStatus "sending" that don't exist in server response.
      // Since we now use client-generated IDs, we can match directly by id.
      const optimisticMessages = prevMessages.filter(
        (m) =>
          m.outboundStatus === "sending" &&
          !mapped.messages.some((sm) => sm.id === m.id),
      );

      // Merge server messages, preserving outboundStatus for messages that were just confirmed
      const mergedMessages = mapped.messages.map((serverMsg) => {
        const prev = prevMessages.find((m) => m.id === serverMsg.id);
        // If we had an optimistic version with "sending" status, mark it as "sent"
        if (prev?.outboundStatus === "sending") {
          return { ...serverMsg, outboundStatus: "sent" as const };
        }
        // If we had a previous version with "sent" status, preserve it
        if (prev?.outboundStatus === "sent") {
          return { ...serverMsg, outboundStatus: "sent" as const };
        }
        return serverMsg;
      });

      // Append optimistic messages that the server doesn't know about yet
      const allMessages = [...mergedMessages, ...optimisticMessages];

      // Deduplicate by ID AND listStableKey to prevent React key errors (defensive measure)
      const seenKeys = new Set<string>();
      const finalMessages = allMessages.filter((m) => {
        const key = m.listStableKey || m.id;
        if (seenKeys.has(key)) {
          console.warn(
            "[threadSlice] Duplicate message key detected, skipping:",
            {
              id: m.id,
              key,
              outboundStatus: m.outboundStatus,
            },
          );
          return false;
        }
        seenKeys.add(key);
        return true;
      });

      state.current = {
        ...mapped,
        title: serverTitle ? mapped.title : state.current.title,
        listIcon: mapped.listIcon ?? state.current.listIcon,
        participants: resolveParticipantsAfterServerMerge(
          state.current.participants,
          mapped.participants,
        ),
        messages: finalMessages,
        blocks: mergeBlocksFromServer(
          state.current.blocks ?? [],
          mapped.blocks ?? [],
        ),
      };

      if (serverTitle) {
        updateThreadTitleInLists(state, mapped.id, {
          title: mapped.title,
          listIcon: mapped.listIcon ?? null,
        });
      }

      syncListParticipantsFromDetail(state.items, state.current);

      // Agent state is now managed via Pusher events (thread.agent.started, thread.agent.completed)
    },
    setListLoading(state, action: PayloadAction<boolean>) {
      state.listLoading = action.payload;
    },
    setItems(
      state,
      action: PayloadAction<
        | {
            items: ThreadListRow[];
            exclusiveFeedItems?: ThreadListRow[];
            campaignItems?: ThreadListRow[];
            nextCursor?: string | null;
            hasMore?: boolean;
            conversationContactCount?: number | null;
          }
        | ThreadListRow[]
      >,
    ) {
      // Support both old format (just items array) and new format (object with pagination)
      if (Array.isArray(action.payload)) {
        state.items = action.payload;
      } else {
        state.items = action.payload.items;
        state.listNextCursor = action.payload.nextCursor ?? null;
        state.listHasMore = action.payload.hasMore ?? false;
        if (action.payload.exclusiveFeedItems !== undefined) {
          state.exclusiveFeedItems = action.payload.exclusiveFeedItems;
        }
        if (action.payload.campaignItems !== undefined) {
          state.campaignItems = action.payload.campaignItems;
        }
        if (action.payload.conversationContactCount !== undefined) {
          state.conversationContactCount =
            action.payload.conversationContactCount;
        }
        state.campaignParticipantPages = {};
      }
      // Set timestamp when list was loaded and clear pending new threads
      state.listLoadedAt = new Date().toISOString();
      state.pendingNewThreads = [];
    },
    appendListItems(
      state,
      action: PayloadAction<{
        items: ThreadListRow[];
        nextCursor: string | null;
        hasMore: boolean;
      }>,
    ) {
      // Append items without duplicates
      const existingIds = new Set(state.items.map((t) => t.id));
      const newItems = action.payload.items.filter(
        (t) => !existingIds.has(t.id),
      );
      state.items = [...state.items, ...newItems];
      state.listNextCursor = action.payload.nextCursor;
      state.listHasMore = action.payload.hasMore;
      state.listLoadingMore = false;
    },
    setListLoadingMore(state, action: PayloadAction<boolean>) {
      state.listLoadingMore = action.payload;
    },
    setCampaignParticipantLoading(
      state,
      action: PayloadAction<{ campaignThreadId: string; loading: boolean }>,
    ) {
      const current = state.campaignParticipantPages[action.payload.campaignThreadId];
      state.campaignParticipantPages[action.payload.campaignThreadId] = {
        items: current?.items ?? [],
        nextCursor: current?.nextCursor ?? null,
        hasMore: current?.hasMore ?? false,
        loading: action.payload.loading,
      };
    },
    setCampaignParticipantPage(
      state,
      action: PayloadAction<{
        campaignThreadId: string;
        items: ThreadListRow[];
        nextCursor: string | null;
        hasMore: boolean;
      }>,
    ) {
      state.campaignParticipantPages[action.payload.campaignThreadId] = {
        items: action.payload.items,
        nextCursor: action.payload.nextCursor,
        hasMore: action.payload.hasMore,
        loading: false,
      };
    },
    appendCampaignParticipants(
      state,
      action: PayloadAction<{
        campaignThreadId: string;
        items: ThreadListRow[];
        nextCursor: string | null;
        hasMore: boolean;
      }>,
    ) {
      const current =
        state.campaignParticipantPages[action.payload.campaignThreadId];
      const existingIds = new Set((current?.items ?? []).map((row) => row.id));
      const nextItems = action.payload.items.filter(
        (row) => !existingIds.has(row.id),
      );
      state.campaignParticipantPages[action.payload.campaignThreadId] = {
        items: [...(current?.items ?? []), ...nextItems],
        nextCursor: action.payload.nextCursor,
        hasMore: action.payload.hasMore,
        loading: false,
      };
    },
    trackNewThread(state, action: PayloadAction<{ threadId: string }>) {
      // Track new thread ID for "new threads" alert instead of prepending
      const { threadId } = action.payload;
      if (
        !state.pendingNewThreads.includes(threadId) &&
        !state.items.some((t) => t.id === threadId)
      ) {
        state.pendingNewThreads.push(threadId);
      }
    },
    clearPendingNewThreads(state) {
      state.pendingNewThreads = [];
    },
    setListError(state, action: PayloadAction<string | null>) {
      state.listError = action.payload;
    },
    prependListRow(state, action: PayloadAction<ThreadListRow>) {
      const row = action.payload;
      state.items = [row, ...state.items.filter((t) => t.id !== row.id)];
    },
    moveThreadToTop(_state, _action: PayloadAction<{ threadId: string }>) {
      // No-op: maintain stable order during session
      // List order only changes on explicit refresh (fetchThreadsList)
    },
    updateThreadActivity(
      state,
      action: PayloadAction<{
        threadId: string;
        lastActivityAt?: string;
        lastActivityType?: string;
        lastMessageAt?: string;
        lastMessagePreview?: string;
      }>,
    ) {
      const {
        threadId,
        lastActivityAt,
        lastActivityType,
        lastMessageAt,
        lastMessagePreview,
      } = action.payload;
      const index = state.items.findIndex((t) => t.id === threadId);
      if (index >= 0) {
        const thread = state.items[index];
        if (thread) {
          // Update activity fields (no reordering - stable order)
          if (lastActivityAt) {
            thread.lastActivityAt = lastActivityAt;
          }
          if (lastActivityType) {
            thread.lastActivityType = lastActivityType;
          }
          if (lastMessageAt) {
            thread.lastMessageAt = lastMessageAt;
          }
          if (lastMessagePreview !== undefined) {
            thread.lastMessagePreview = lastMessagePreview;
          }
        }
      }
      // Also update current thread if it matches
      if (state.current?.id === threadId) {
        if (lastActivityAt) {
          (state.current as { lastActivityAt?: string }).lastActivityAt =
            lastActivityAt;
        }
        if (lastActivityType) {
          (state.current as { lastActivityType?: string }).lastActivityType =
            lastActivityType;
        }
        if (lastMessageAt) {
          state.current.lastMessageAt = lastMessageAt;
        }
        if (lastMessagePreview !== undefined) {
          state.current.lastMessagePreview = lastMessagePreview;
        }
      }
    },
    updateThreadStatus(
      state,
      action: PayloadAction<{
        threadId: string;
        hasActiveWorkflow?: boolean;
        hasActiveAgent?: boolean;
        pendingApprovalCount?: number;
        hasScheduledMessage?: boolean;
        nextScheduledAt?: string | null;
      }>,
    ) {
      const {
        threadId,
        hasActiveWorkflow,
        hasActiveAgent,
        pendingApprovalCount,
        hasScheduledMessage,
        nextScheduledAt,
      } = action.payload;
      const index = state.items.findIndex((t) => t.id === threadId);
      if (index >= 0) {
        const thread = state.items[index];
        if (thread) {
          if (hasActiveWorkflow !== undefined) {
            thread.hasActiveWorkflow = hasActiveWorkflow;
          }
          if (hasActiveAgent !== undefined) {
            thread.hasActiveAgent = hasActiveAgent;
          }
          if (pendingApprovalCount !== undefined) {
            thread.pendingApprovalCount = pendingApprovalCount;
          }
          if (hasScheduledMessage !== undefined) {
            thread.hasScheduledMessage = hasScheduledMessage;
          }
          if (nextScheduledAt !== undefined) {
            thread.nextScheduledAt = nextScheduledAt;
          }
        }
      }
    },
    setSendingMessage(state, action: PayloadAction<boolean>) {
      state.sendingMessage = action.payload;
    },
    beginTitleStream(state, action: PayloadAction<string>) {
      state.titleStreamRequestId += 1;
      state.titleStreamActive = true;
      state.titleStreamText = "";
      state.titleStreamThreadId = action.payload;
    },
    titleStreamDelta(
      state,
      action: PayloadAction<{ text: string; requestId: number }>,
    ) {
      if (action.payload.requestId !== state.titleStreamRequestId) return;
      state.titleStreamText = action.payload.text;
    },
    endTitleStream(state, action: PayloadAction<number>) {
      if (action.payload !== state.titleStreamRequestId) return;
      state.titleStreamActive = false;
      state.titleStreamText = "";
      state.titleStreamThreadId = null;
    },
    primeTitleStreamUi(state, action: PayloadAction<string>) {
      state.titleStreamRequestId += 1;
      state.titleStreamActive = true;
      state.titleStreamText = "";
      state.titleStreamThreadId = action.payload;
    },
    applyEnrichedTitle(
      state,
      action: PayloadAction<{
        threadId: string;
        title: string;
        listIcon?: string;
        requestId: number;
      }>,
    ) {
      const { threadId, title, listIcon, requestId } = action.payload;
      if (requestId !== state.titleStreamRequestId) return;
      if (state.current?.id === threadId) {
        state.current = {
          ...state.current,
          title,
          listIcon: listIcon !== undefined ? listIcon : state.current.listIcon,
        };
      }
      updateThreadTitleInLists(state, threadId, { title, listIcon });
    },
    patchThreadListMeta(
      state,
      action: PayloadAction<{
        threadId: string;
        title?: string | null;
        listIcon?: string | null;
      }>,
    ) {
      const { threadId, title, listIcon } = action.payload;
      if (state.current?.id === threadId) {
        state.current = {
          ...state.current,
          ...(title !== undefined ? { title } : {}),
          ...(listIcon !== undefined ? { listIcon } : {}),
        };
      }
      updateThreadTitleInLists(state, threadId, { title, listIcon });
    },
    markMessageOutboundFailed(
      state,
      action: PayloadAction<{ messageId: string }>,
    ) {
      if (!state.current) return;
      const id = action.payload.messageId;
      state.current = {
        ...state.current,
        messages: state.current.messages.map((m) =>
          m.id === id ? { ...m, outboundStatus: "failed" as const } : m,
        ),
        blocks: (state.current.blocks ?? []).map((block) => {
          const optimistic = block as ThreadBlock & {
            _optimistic?: boolean;
            _messageId?: string;
          };
          const matches =
            optimistic._messageId === id ||
            (block.message?.id === id &&
              (optimistic._optimistic ||
                block.message?.sendStatus === "SENDING"));
          if (!matches || !block.message) {
            return block;
          }
          return {
            ...block,
            message: {
              ...block.message,
              sendStatus: "FAILED" as const,
            },
          };
        }),
      };
    },
    confirmOptimisticMessage(
      state,
      action: PayloadAction<{
        threadId: string;
        messageId: string;
        createdAt?: string;
        sender?: ThreadMessage["sender"] | null;
        content?: string;
        contentType?: ThreadMessage["contentType"];
        aiGenerated?: boolean;
      }>,
    ) {
      if (!state.current || state.current.id !== action.payload.threadId) {
        return;
      }
      const {
        messageId,
        createdAt,
        sender,
        content,
        contentType,
        aiGenerated,
      } = action.payload;

      state.current.blocks = (state.current.blocks ?? []).map((block) => {
        const optimistic = block as ThreadBlock & {
          _optimistic?: boolean;
          _messageId?: string;
        };
        const matches =
          optimistic._messageId === messageId ||
          block.message?.id === messageId;
        if (!matches || !block.message) {
          return block;
        }

        const nextBlock: ThreadBlock = {
          ...block,
          createdAt: createdAt ?? block.createdAt,
          updatedAt: createdAt ?? block.updatedAt,
          participant: sender ?? block.participant,
          message: {
            ...block.message,
            id: messageId,
            content: content ?? block.message.content,
            contentType: contentType ?? block.message.contentType,
            aiGenerated: aiGenerated ?? block.message.aiGenerated,
            sender: sender ?? block.message.sender,
            sendStatus: "SENT",
          },
        };
        delete (nextBlock as ThreadBlock & { _optimistic?: boolean })
          ._optimistic;
        delete (nextBlock as ThreadBlock & { _messageId?: string })._messageId;
        return nextBlock;
      });

      state.current.messages = state.current.messages.map((m) =>
        m.id === messageId
          ? {
              ...m,
              createdAt: createdAt ?? m.createdAt,
              content: content ?? m.content,
              contentType: contentType ?? m.contentType,
              aiGenerated: aiGenerated ?? m.aiGenerated,
              sender: sender ?? m.sender,
              outboundStatus: "sent" as const,
              sendStatus: "SENT" as const,
            }
          : m,
      );
    },
    senderSnapshotSet(state, action: PayloadAction<SenderSnapshot | null>) {
      state.senderSnapshot = action.payload;
      state.senderSnapshotLoaded = true;
    },
    setAgentProcessing(
      state,
      action: PayloadAction<{
        threadId: string;
        agentRunId: string;
        agentId: string;
        agentName?: string;
        agentKind?: string | null;
        isProcessing: boolean;
      }>,
    ) {
      const {
        threadId,
        agentRunId,
        agentId,
        agentName,
        agentKind,
        isProcessing,
      } = action.payload;
      if (isProcessing) {
        state.agentState[threadId] = {
          isProcessing: true,
          agentRunId,
          agentId,
          agentName: agentName ?? null,
          agentKind: agentKind ?? null,
          thought: null,
          toolCalls: [],
        };
      } else {
        const existing = state.agentState[threadId];
        if (existing && existing.agentRunId === agentRunId) {
          state.agentState[threadId] = {
            ...existing,
            isProcessing: false,
          };
        }
      }
    },
    updateAgentThought(
      state,
      action: PayloadAction<{
        threadId: string;
        agentRunId: string;
        thought: string;
        isThinking: boolean;
        durationMs?: number;
        agentName?: string;
      }>,
    ) {
      const {
        threadId,
        agentRunId,
        thought,
        isThinking,
        durationMs,
        agentName,
      } = action.payload;
      const existing = state.agentState[threadId];
      if (existing && existing.agentRunId === agentRunId) {
        state.agentState[threadId] = {
          ...existing,
          // Update agent name if provided (for child agents in multi-stage workflows)
          agentName: agentName ?? existing.agentName,
          thought: {
            content: thought,
            isThinking,
            durationMs,
          },
        };
      }
    },
    updateAgentToolCall(
      state,
      action: PayloadAction<{
        threadId: string;
        agentRunId: string;
        toolCall: AgentToolCall;
        agentName?: string;
      }>,
    ) {
      const { threadId, agentRunId, toolCall, agentName } = action.payload;
      const existing = state.agentState[threadId];
      if (existing && existing.agentRunId === agentRunId) {
        // Update agent name if provided (for child agents in multi-stage workflows)
        if (agentName) {
          existing.agentName = agentName;
        }
        const existingIndex = existing.toolCalls.findIndex(
          (tc) => tc.id === toolCall.id,
        );
        if (existingIndex >= 0) {
          const previous = existing.toolCalls[existingIndex];
          if (!previous) {
            existing.toolCalls.push(toolCall);
          } else {
            existing.toolCalls[existingIndex] = {
              ...previous,
              ...toolCall,
              name: toolCall.description || toolCall.name || previous.name,
              description: toolCall.description ?? previous.description,
            };
          }
        } else {
          existing.toolCalls.push(toolCall);
        }
      }
    },
    clearAgentState(state, action: PayloadAction<{ threadId: string }>) {
      delete state.agentState[action.payload.threadId];
    },
    restoreAgentStateFromTrace(
      state,
      action: PayloadAction<{
        threadId: string;
        agentRunId: string;
        agentId: string;
        agentName: string;
        trace: {
          events?: TraceEvent[];
        } | null;
      }>,
    ) {
      const { threadId, agentRunId, agentId, agentName, trace } =
        action.payload;
      state.agentState[threadId] = buildAgentStateFromTrace(
        agentRunId,
        agentId,
        agentName,
        trace?.events,
      );
    },
    appendMessageFromRealtime(
      state,
      action: PayloadAction<{
        threadId: string;
        message: {
          id: string;
          content: string;
          contentType: string;
          createdAt: string | Date;
          aiGenerated: boolean;
          sender: {
            id: string;
            displayName: string;
            avatarUrl: string | null;
            kind: string;
          } | null;
        };
      }>,
    ) {
      const { threadId, message } = action.payload;

      // Only update if we're viewing this thread
      if (!state.current || state.current.id !== threadId) {
        return;
      }

      // Check if message already exists (avoid duplicates from optimistic updates or re-delivery)
      const messageExists = state.current.messages.some(
        (m) => m.id === message.id,
      );
      if (messageExists) {
        return;
      }

      const createdAt =
        typeof message.createdAt === "string"
          ? message.createdAt
          : message.createdAt.toISOString();

      const newMessage: ThreadMessage = {
        id: message.id,
        listStableKey: message.id,
        content: message.content,
        contentType: message.contentType as ThreadMessage["contentType"],
        createdAt,
        aiGenerated: message.aiGenerated,
        sender: message.sender
          ? {
              id: message.sender.id,
              displayName: message.sender.displayName,
              avatarUrl: message.sender.avatarUrl,
              kind: message.sender.kind as
                | "MEMBER"
                | "CONTACT"
                | "AGENT"
                | "SYSTEM",
            }
          : {
              id: "system",
              displayName: "System",
              avatarUrl: null,
              kind: "SYSTEM" as const,
            },
        attachments: [],
      };

      state.current = {
        ...state.current,
        messages: [...state.current.messages, newMessage],
        lastMessageAt: createdAt,
        lastMessagePreview: message.content,
      };

      // Also update the thread in the list
      state.items = state.items.map((item) =>
        item.id === threadId
          ? {
              ...item,
              lastMessageAt: createdAt,
              lastMessagePreview: message.content,
            }
          : item,
      );
    },
    prependThreadFromRealtime(
      state,
      action: PayloadAction<{
        thread: {
          id: string;
          title: string | null;
          status: string;
          kind: string;
          lastMessageAt: string | null;
          lastMessagePreview: string | null;
          lastActivityAt?: string | null;
          lastActivityType?: string | null;
          createdAt: string;
          parentId?: string | null;
        };
      }>,
    ) {
      const { thread } = action.payload;

      // Check if thread already exists in the list
      const threadExists = state.items.some((item) => item.id === thread.id);
      if (threadExists) {
        return;
      }

      const newRow: ThreadListRow = {
        id: thread.id,
        title: thread.title,
        status: thread.status as ThreadListRow["status"],
        kind: thread.kind as ThreadListRow["kind"],
        lastMessageAt: thread.lastMessageAt,
        lastMessagePreview: thread.lastMessagePreview,
        lastActivityAt: thread.lastActivityAt ?? thread.createdAt,
        lastActivityType: thread.lastActivityType ?? "created",
        unreadCount: 1,
        listIcon: null,
        participants: [],
        parentId: thread.parentId ?? null,
        subThreadCount: 0,
      };

      state.items = [newRow, ...state.items];
    },
    updateThreadWorkflowRun(
      state,
      action: PayloadAction<{
        threadId: string;
        workflowRunId: string;
        threadWorkflowRunId?: string;
        sourceMessageId?: string | null;
        status?: "RUNNING" | "COMPLETED" | "CANCELLED" | "FAILED";
        workflowRun?: {
          id: string;
          workflow: { name: string };
          steps: Array<{
            id: string;
            handle: string;
            name: string;
            status: string;
            sortOrder: number;
            startedAt: string | null;
            completedAt: string | null;
          }>;
        };
      }>,
    ) {
      const {
        threadId,
        workflowRunId,
        threadWorkflowRunId,
        sourceMessageId,
        status,
        workflowRun,
      } = action.payload;

      // Update the current thread if it matches
      if (state.current?.id === threadId) {
        if (!state.current.workflowRuns) {
          state.current.workflowRuns = [];
        }

        const workflowIndex = state.current.workflowRuns.findIndex(
          (wr) => wr.workflowRunId === workflowRunId || wr.id === workflowRunId,
        );

        if (workflowIndex !== -1) {
          const existing = state.current.workflowRuns[workflowIndex]!;
          if (threadWorkflowRunId && existing.id !== threadWorkflowRunId) {
            existing.id = threadWorkflowRunId;
          }
          if (sourceMessageId && !existing.sourceMessageId) {
            existing.sourceMessageId = sourceMessageId;
          }
          // Update existing workflow
          if (status) {
            existing.status = status;
          }
          if (workflowRun) {
            const steps = workflowRun.steps ?? [];
            const stepsCompleted = steps.filter(
              (s) => s.status === "COMPLETED" || s.status === "SKIPPED",
            ).length;
            existing.workflowRun =
              workflowRun as ThreadWorkflowRunSummary["workflowRun"];
            existing.stepsCompleted = stepsCompleted;
            existing.stepsTotal = steps.length;
          }
        } else if (workflowRun) {
          // Add new workflow if we have full workflow data and it doesn't exist
          const steps = workflowRun.steps ?? [];
          const stepsCompleted = steps.filter(
            (s) => s.status === "COMPLETED" || s.status === "SKIPPED",
          ).length;
          const newWorkflowRun: ThreadWorkflowRunSummary = {
            id: threadWorkflowRunId ?? workflowRunId,
            handle: workflowRun.id,
            status: status ?? "RUNNING",
            startedAt: new Date().toISOString(),
            workflowRunId: workflowRunId,
            workflowName: workflowRun.workflow.name,
            stepsCompleted,
            stepsTotal: steps.length,
            sourceMessageId: sourceMessageId ?? null,
            workflowRun: workflowRun as ThreadWorkflowRunSummary["workflowRun"],
          };
          state.current.workflowRuns.push(newWorkflowRun);
        }
      }
    },
    appendWorkflowRunFromRealtime(
      state,
      action: PayloadAction<{
        threadId: string;
        workflowRun: ThreadWorkflowRunSummary;
      }>,
    ) {
      const { threadId, workflowRun } = action.payload;
      if (state.current?.id !== threadId) return;

      if (!state.current.workflowRuns) {
        state.current.workflowRuns = [];
      }

      const existingIndex = state.current.workflowRuns.findIndex(
        (wr) =>
          wr.id === workflowRun.id ||
          (workflowRun.workflowRunId != null &&
            wr.workflowRunId === workflowRun.workflowRunId),
      );
      if (existingIndex !== -1) {
        const existing = state.current.workflowRuns[existingIndex]!;
        if (workflowRun.sourceMessageId && !existing.sourceMessageId) {
          existing.sourceMessageId = workflowRun.sourceMessageId;
        }
        if (workflowRun.id && existing.id !== workflowRun.id) {
          existing.id = workflowRun.id;
        }
        return;
      }

      state.current.workflowRuns.push(workflowRun);
    },
    appendSubThreadFromRealtime(
      state,
      action: PayloadAction<{
        threadId: string;
        subThread: SubThreadSummary;
      }>,
    ) {
      const { threadId, subThread } = action.payload;
      if (state.current?.id !== threadId) return;

      if (!state.current.subThreads) {
        state.current.subThreads = [];
      }

      const existingIndex = state.current.subThreads.findIndex(
        (st) => st.id === subThread.id,
      );
      if (existingIndex !== -1) {
        const existing = state.current.subThreads[existingIndex]!;
        if (subThread.sourceMessageId && !existing.sourceMessageId) {
          existing.sourceMessageId = subThread.sourceMessageId;
        }
        if (subThread.title && !existing.title) {
          existing.title = subThread.title;
        }
        return;
      }

      state.current.subThreads.push(subThread);
    },
    // New thread routing reducers
    setNewThreadType(state, action: PayloadAction<NewThreadType>) {
      state.newThreadType = action.payload;
      // Reset routing fields when switching to internal
      if (action.payload === "internal") {
        state.selectedChannelId = null;
        state.routingAddress = "";
      }
    },
    setSelectedChannelId(state, action: PayloadAction<string | null>) {
      state.selectedChannelId = action.payload;
    },
    setRoutingAddress(state, action: PayloadAction<string>) {
      state.routingAddress = action.payload;
    },
    resetNewThreadState(state) {
      state.newThreadType = "internal";
      state.selectedChannelId = null;
      state.routingAddress = "";
    },
    setPendingMessageDraft(
      state,
      action: PayloadAction<{ threadId: string; content: string } | null>,
    ) {
      state.pendingMessageDraft = action.payload;
    },
    clearPendingMessageDraft(state) {
      state.pendingMessageDraft = null;
    },
    // Message pagination reducers (bidirectional)
    setLoadingOlderMessages(state, action: PayloadAction<boolean>) {
      state.loadingOlderMessages = action.payload;
    },
    setLoadingNewerMessages(state, action: PayloadAction<boolean>) {
      state.loadingNewerMessages = action.payload;
    },
    setBidirectionalPagination(
      state,
      action: PayloadAction<{
        hasMoreBefore: boolean;
        hasMoreAfter: boolean;
        beforeCursor: string | null;
        afterCursor: string | null;
      }>,
    ) {
      state.hasMoreBefore = action.payload.hasMoreBefore;
      state.hasMoreAfter = action.payload.hasMoreAfter;
      state.beforeCursor = action.payload.beforeCursor;
      state.afterCursor = action.payload.afterCursor;
    },
    prependMessages(
      state,
      action: PayloadAction<{
        messages: ThreadMessage[];
        hasMore: boolean;
        nextCursor: string | null;
      }>,
    ) {
      if (!state.current) return;
      // Prepend older messages to the beginning
      const existingIds = new Set(state.current.messages.map((m) => m.id));
      const newMessages = action.payload.messages.filter(
        (m) => !existingIds.has(m.id),
      );
      state.current.messages = [...newMessages, ...state.current.messages];
      state.hasMoreBefore = action.payload.hasMore;
      state.beforeCursor = action.payload.nextCursor;
      state.loadingOlderMessages = false;
    },
    prependBlocks(
      state,
      action: PayloadAction<{
        blocks: ThreadBlock[];
        hasMore: boolean;
        nextCursor: string | null;
      }>,
    ) {
      if (!state.current) return;
      // Prepend older blocks to the beginning
      const existingIds = new Set(state.current.blocks?.map((b) => b.id) ?? []);
      const newBlocks = action.payload.blocks.filter(
        (b) => !existingIds.has(b.id),
      );
      state.current.blocks = [...newBlocks, ...(state.current.blocks ?? [])];
      state.hasMoreBefore = action.payload.hasMore;
      state.beforeCursor = action.payload.nextCursor;
      state.loadingOlderMessages = false;
    },
    appendMessages(
      state,
      action: PayloadAction<{
        messages: ThreadMessage[];
        hasMore: boolean;
        nextCursor: string | null;
      }>,
    ) {
      if (!state.current) return;
      // Append newer messages to the end
      const existingIds = new Set(state.current.messages.map((m) => m.id));
      const newMessages = action.payload.messages.filter(
        (m) => !existingIds.has(m.id),
      );
      state.current.messages = [...state.current.messages, ...newMessages];
      state.hasMoreAfter = action.payload.hasMore;
      state.afterCursor = action.payload.nextCursor;
      state.loadingNewerMessages = false;
    },
    appendBlocks(
      state,
      action: PayloadAction<{
        blocks: ThreadBlock[];
        hasMore: boolean;
        nextCursor: string | null;
      }>,
    ) {
      if (!state.current) return;
      // Append newer blocks to the end
      const existingIds = new Set(state.current.blocks?.map((b) => b.id) ?? []);
      const newBlocks = action.payload.blocks.filter(
        (b) => !existingIds.has(b.id),
      );
      state.current.blocks = [...(state.current.blocks ?? []), ...newBlocks];
      state.hasMoreAfter = action.payload.hasMore;
      state.afterCursor = action.payload.nextCursor;
      state.loadingNewerMessages = false;
    },
    // Append an optimistic block while message is being sent
    appendOptimisticBlock(
      state,
      action: PayloadAction<{
        threadId: string;
        block: ThreadBlock & { _optimistic?: boolean };
        messageId: string;
      }>,
    ) {
      const { threadId, block, messageId } = action.payload;
      if (!state.current || state.current.id !== threadId) {
        return;
      }

      if (!state.current.blocks) {
        state.current.blocks = [];
      }

      // Mark as optimistic for UI styling
      const optimisticBlock = {
        ...block,
        _optimistic: true,
        _messageId: messageId,
      };
      state.current.blocks.push(optimisticBlock as ThreadBlock);
    },
    // Block-related reducers for ThreadBlock timeline
    appendBlockFromRealtime(
      state,
      action: PayloadAction<{
        threadId: string;
        block: ThreadBlock;
      }>,
    ) {
      const { threadId, block } = action.payload;
      if (!state.current || state.current.id !== threadId) {
        return;
      }

      // Initialize blocks array if not present
      if (!state.current.blocks) {
        state.current.blocks = [];
      }

      // Check if this block matches an optimistic block by messageId
      // The server-confirmed block will have the same message.id as our optimistic block
      const messageId = block.message?.id;
      if (messageId) {
        const optimisticIndex = state.current.blocks.findIndex(
          (b) =>
            (b as ThreadBlock & { _messageId?: string })._messageId ===
              messageId ||
            (b.message?.id === messageId &&
              (b as ThreadBlock & { _optimistic?: boolean })._optimistic),
        );
        if (optimisticIndex !== -1) {
          // Replace optimistic block with server-confirmed block
          state.current.blocks[optimisticIndex] = block;
          return;
        }
      }

      // Check if block already exists at top level
      const blockExists = state.current.blocks.some((b) => b.id === block.id);
      if (blockExists) {
        return;
      }

      // If this block has a parentBlockId, add it as a child to the parent block
      if (block.parentBlockId) {
        const parentBlock = state.current.blocks.find(
          (b) => b.id === block.parentBlockId,
        );
        if (parentBlock) {
          // Check if child already exists
          const childExists = parentBlock.childBlocks?.some(
            (c) => c.id === block.id,
          );
          if (!childExists) {
            // Initialize childBlocks if not present
            if (!parentBlock.childBlocks) {
              parentBlock.childBlocks = [];
            }
            // Add as child block (convert ThreadBlock to ThreadBlockChild format)
            parentBlock.childBlocks.push({
              id: block.id,
              kind: block.kind,
              sequence: block.sequence,
              status: block.status,
              content: block.content,
              contentStructured: block.contentStructured,
              createdAt: block.createdAt,
              durationMs: null,
              participant: block.participant,
              agentRun: block.agentRun,
              metadata: block.metadata,
            });
          }
        }
        // Don't add child blocks to top level
        return;
      }

      // Insert block in sequence order (top-level blocks only)
      const insertIndex = state.current.blocks.findIndex(
        (b) => b.sequence > block.sequence,
      );
      if (insertIndex === -1) {
        state.current.blocks.push(block);
      } else {
        state.current.blocks.splice(insertIndex, 0, block);
      }
    },
    updateBlockFromRealtime(
      state,
      action: PayloadAction<{
        threadId: string;
        blockId: string;
        updates: Partial<ThreadBlock>;
      }>,
    ) {
      const { threadId, blockId, updates } = action.payload;
      if (!state.current || state.current.id !== threadId) {
        return;
      }

      if (!state.current.blocks) {
        return;
      }

      // First, try to find and update at top level
      const blockIndex = state.current.blocks.findIndex(
        (b) => b.id === blockId,
      );
      if (blockIndex !== -1) {
        const existingBlock = state.current.blocks[blockIndex];
        if (existingBlock) {
          state.current.blocks[blockIndex] = {
            ...existingBlock,
            ...updates,
          } as ThreadBlock;
        }
        return;
      }

      // If not found at top level, search in childBlocks of all parent blocks
      for (let i = 0; i < state.current.blocks.length; i++) {
        const parentBlock = state.current.blocks[i];
        if (parentBlock && parentBlock.childBlocks) {
          const childIndex = parentBlock.childBlocks.findIndex(
            (c) => c.id === blockId,
          );
          if (childIndex !== -1) {
            const existingChild = parentBlock.childBlocks[childIndex];
            if (existingChild) {
              // Create new childBlocks array with updated child
              const newChildBlocks = [...parentBlock.childBlocks];
              newChildBlocks[childIndex] = {
                ...existingChild,
                ...updates,
              } as ThreadBlockChild;
              // Create new parent block with new childBlocks
              state.current.blocks[i] = {
                ...parentBlock,
                childBlocks: newChildBlocks,
              } as ThreadBlock;
            }
            return;
          }
          for (const child of parentBlock.childBlocks) {
            const nested = child.childBlocks ?? [];
            const nestedIndex = nested.findIndex((item) => item.id === blockId);
            if (nestedIndex === -1) continue;
            const existingNested = nested[nestedIndex];
            if (!existingNested) continue;
            const newNested = [...nested];
            newNested[nestedIndex] = {
              ...existingNested,
              ...updates,
            } as ThreadBlockChild;
            const newChildBlocks = parentBlock.childBlocks.map((item) =>
              item.id === child.id ? { ...item, childBlocks: newNested } : item,
            );
            state.current.blocks[i] = {
              ...parentBlock,
              childBlocks: newChildBlocks,
            } as ThreadBlock;
            return;
          }
        }
      }
    },
    setCallTranscript(
      state,
      action: PayloadAction<{
        threadId: string;
        callSessionId: string;
        messages: ThreadMessage[];
        status?: NonNullable<ThreadBlock["callSession"]>["status"];
        append?: boolean;
      }>,
    ) {
      const { threadId, callSessionId, messages, status, append } =
        action.payload;
      if (!state.current || state.current.id !== threadId) {
        return;
      }

      const blocks = state.current.blocks;
      if (!blocks) return;

      const block = blocks.find((b) => b.callSession?.id === callSessionId);
      if (!block?.callSession) {
        return;
      }

      block.callSession = {
        ...block.callSession,
        ...(status ? { status } : {}),
        transcriptMessages: append
          ? [...block.callSession.transcriptMessages, ...messages]
          : messages,
        lastTranscriptMessage:
          messages.at(-1) ?? block.callSession.lastTranscriptMessage ?? null,
      };
    },
    setBlocks(
      state,
      action: PayloadAction<{
        threadId: string;
        blocks: ThreadBlock[];
      }>,
    ) {
      const { threadId, blocks } = action.payload;
      if (!state.current || state.current.id !== threadId) {
        return;
      }
      state.current.blocks = blocks;
    },
    // Thread sidebar actions
    setThreadSidebarFilter(
      state,
      action: PayloadAction<ThreadSidebarFilterType>,
    ) {
      state.sidebarFilter = action.payload;
    },
    setThreadSidebarSearch(state, action: PayloadAction<string>) {
      state.sidebarSearch = action.payload;
    },
    setThreadSidebarContactSort(
      state,
      action: PayloadAction<ThreadSidebarContactSort>,
    ) {
      state.sidebarContactSort = action.payload;
    },
    setThreadSidebarChannel(state, action: PayloadAction<string | null>) {
      state.sidebarChannelId = action.payload;
    },
    resetThreadSidebarState(state) {
      state.sidebarFilter = "inbox";
      state.sidebarSearch = "";
      state.sidebarContactSort = "last_activity";
      state.sidebarChannelId = null;
    },
    setThreadPinned(
      state,
      action: PayloadAction<{ threadId: string; pinnedAt: string | null }>,
    ) {
      const { threadId, pinnedAt } = action.payload;
      const thread = state.items.find((item) => item.id === threadId);
      if (thread) {
        thread.pinnedAt = pinnedAt;
      }
    },
    setRecipientOptedOut(
      state,
      action: PayloadAction<{ threadId: string; recipientOptedOut: boolean }>,
    ) {
      if (state.current?.id === action.payload.threadId) {
        state.current.recipientOptedOut = action.payload.recipientOptedOut;
      }
    },
    patchCampaignBatchJob(
      state,
      action: PayloadAction<{
        threadId: string;
        batchJobId: string;
        rootBatchJobId?: string;
      }>,
    ) {
      const { threadId, batchJobId, rootBatchJobId } = action.payload;
      if (state.current?.id === threadId) {
        state.current.campaignBatchJobId = batchJobId;
        if (rootBatchJobId) {
          state.current.campaignRootBatchJobId = rootBatchJobId;
        }
      }
    },
  },
});

export const {
  applyRouteNavigation,
  clearCurrentThread,
  setDetailLoading,
  setCurrent,
  mergeCurrentFromServer,
  mergeCurrentParticipants,
  setListLoading,
  setItems,
  setListError,
  prependListRow,
  moveThreadToTop,
  updateThreadActivity,
  updateThreadStatus,
  setRecipientOptedOut,
  setSendingMessage,
  beginTitleStream,
  titleStreamDelta,
  endTitleStream,
  primeTitleStreamUi,
  applyEnrichedTitle,
  patchThreadListMeta,
  markMessageOutboundFailed,
  confirmOptimisticMessage,
  senderSnapshotSet,
  setAgentProcessing,
  updateAgentThought,
  updateAgentToolCall,
  clearAgentState,
  restoreAgentStateFromTrace,
  appendMessageFromRealtime,
  prependThreadFromRealtime,
  updateThreadWorkflowRun,
  appendWorkflowRunFromRealtime,
  appendSubThreadFromRealtime,
  // New thread routing actions
  setNewThreadType,
  setSelectedChannelId,
  setRoutingAddress,
  resetNewThreadState,
  // Pending message draft actions
  setPendingMessageDraft,
  clearPendingMessageDraft,
  // Message pagination actions (bidirectional)
  setLoadingOlderMessages,
  setLoadingNewerMessages,
  setBidirectionalPagination,
  prependMessages,
  prependBlocks,
  appendMessages,
  appendBlocks,
  // Stable list order actions
  appendListItems,
  setListLoadingMore,
  setCampaignParticipantLoading,
  setCampaignParticipantPage,
  appendCampaignParticipants,
  trackNewThread,
  clearPendingNewThreads,
  // Block-related actions
  appendOptimisticBlock,
  appendBlockFromRealtime,
  updateBlockFromRealtime,
  setCallTranscript,
  setBlocks,
  // Thread sidebar actions
  setThreadSidebarFilter,
  setThreadSidebarSearch,
  setThreadSidebarContactSort,
  setThreadSidebarChannel,
  resetThreadSidebarState,
  setThreadPinned,
  patchCampaignBatchJob,
} = threadsSlice.actions;

const THREAD_LIST_PAGE_SIZE = 50;

let threadListFetchSeq = 0;
let sidebarSearchTimer: ReturnType<typeof setTimeout> | null = null;

function threadListQuery(state: RootState, cursor?: string) {
  const search = state.threads.sidebarSearch.trim();
  return {
    limit: THREAD_LIST_PAGE_SIZE,
    includeArchived: false as const,
    includeSubthreads: true,
    excludeCampaignRecipients: true,
    ...(search ? { search } : {}),
    ...(cursor ? { cursor } : {}),
  };
}

function applyThreadListPage(
  dispatch: (action: ReturnType<typeof setItems>) => void,
  data: {
    items?: Parameters<typeof mapListItems>[0];
    exclusiveFeeds?: Parameters<typeof mapListItems>[0];
    campaignThreads?: Parameters<typeof mapListItems>[0];
    nextCursor?: string | null;
    hasMore?: boolean;
    conversationContactCount?: number | null;
  },
) {
  dispatch(
    setItems({
      items: mapListItems(data.items ?? []),
      exclusiveFeedItems: mapListItems(data.exclusiveFeeds ?? []),
      campaignItems: mapListItems(data.campaignThreads ?? []),
      nextCursor: data.nextCursor ?? null,
      hasMore: data.hasMore ?? false,
      conversationContactCount: data.conversationContactCount ?? null,
    }),
  );
}

export const fetchThreadsList = createAsyncThunk(
  "threads/fetchThreadsList",
  async (_: void, { dispatch, getState }) => {
    const seq = ++threadListFetchSeq;
    const state = getState() as RootState;
    const hasCachedItems = state.threads.items.length > 0;
    if (!hasCachedItems) {
      dispatch(setListLoading(true));
    }
    dispatch(setListError(null));
    try {
      const data = await trpc.threads.list.query(threadListQuery(state));
      if (seq !== threadListFetchSeq) return;
      applyThreadListPage(dispatch, data);
    } catch (e) {
      if (seq !== threadListFetchSeq) return;
      console.error("Failed to fetch threads:", e);
      dispatch(setListError("Failed to load threads"));
    } finally {
      if (seq === threadListFetchSeq) {
        dispatch(setListLoading(false));
      }
    }
  },
);

export const fetchThreadsListSoft = createAsyncThunk(
  "threads/fetchThreadsListSoft",
  async (_, { dispatch, getState }) => {
    const seq = ++threadListFetchSeq;
    const state = getState() as RootState;
    try {
      const data = await trpc.threads.list.query(threadListQuery(state));
      if (seq !== threadListFetchSeq) return;
      applyThreadListPage(dispatch, data);
    } catch (e) {
      console.error("Failed to soft-fetch threads:", e);
    }
  },
);

const CAMPAIGN_PARTICIPANT_PAGE_SIZE = 25;

export const fetchCampaignParticipants = createAsyncThunk(
  "threads/fetchCampaignParticipants",
  async (campaignThreadId: string, { dispatch, getState }) => {
    const state = getState() as RootState;
    const existing = state.threads.campaignParticipantPages[campaignThreadId];
    if (existing && (existing.items.length > 0 || existing.loading)) return;

    dispatch(
      setCampaignParticipantLoading({ campaignThreadId, loading: true }),
    );
    try {
      const data = await trpc.threads.listCampaignParticipants.query({
        campaignThreadId,
        limit: CAMPAIGN_PARTICIPANT_PAGE_SIZE,
        search: state.threads.sidebarSearch.trim() || undefined,
      });
      dispatch(
        setCampaignParticipantPage({
          campaignThreadId,
          items: mapListItems(data.items ?? []),
          nextCursor: data.nextCursor ?? null,
          hasMore: data.hasMore ?? false,
        }),
      );
    } catch (e) {
      console.error("Failed to fetch campaign participants:", e);
      dispatch(
        setCampaignParticipantLoading({ campaignThreadId, loading: false }),
      );
    }
  },
);

export const fetchMoreCampaignParticipants = createAsyncThunk(
  "threads/fetchMoreCampaignParticipants",
  async (campaignThreadId: string, { dispatch, getState }) => {
    const state = getState() as RootState;
    const page = state.threads.campaignParticipantPages[campaignThreadId];
    if (!page || !page.hasMore || page.loading || !page.nextCursor) return;

    dispatch(
      setCampaignParticipantLoading({ campaignThreadId, loading: true }),
    );
    try {
      const data = await trpc.threads.listCampaignParticipants.query({
        campaignThreadId,
        cursor: page.nextCursor,
        limit: CAMPAIGN_PARTICIPANT_PAGE_SIZE,
        search: state.threads.sidebarSearch.trim() || undefined,
      });
      dispatch(
        appendCampaignParticipants({
          campaignThreadId,
          items: mapListItems(data.items ?? []),
          nextCursor: data.nextCursor ?? null,
          hasMore: data.hasMore ?? false,
        }),
      );
    } catch (e) {
      console.error("Failed to fetch more campaign participants:", e);
      dispatch(
        setCampaignParticipantLoading({ campaignThreadId, loading: false }),
      );
    }
  },
);

export const fetchMoreThreads = createAsyncThunk(
  "threads/fetchMoreThreads",
  async (_, { dispatch, getState }) => {
    const seq = threadListFetchSeq;
    const state = getState() as RootState;
    const { listNextCursor, listHasMore, listLoadingMore } = state.threads;
    const searchAtStart = state.threads.sidebarSearch.trim();

    if (!listNextCursor || !listHasMore || listLoadingMore) {
      return;
    }

    dispatch(setListLoadingMore(true));

    try {
      const data = await trpc.threads.list.query(
        threadListQuery(state, listNextCursor),
      );
      const latest = getState() as RootState;
      if (
        seq !== threadListFetchSeq ||
        latest.threads.sidebarSearch.trim() !== searchAtStart
      ) {
        dispatch(setListLoadingMore(false));
        return;
      }

      dispatch(
        appendListItems({
          items: mapListItems(data.items ?? []),
          nextCursor: data.nextCursor ?? null,
          hasMore: data.hasMore ?? false,
        }),
      );
    } catch (e) {
      console.error("Failed to fetch more threads:", e);
      dispatch(setListLoadingMore(false));
    }
  },
);

/** Update the sidebar query and reload the list from the first page. */
export function queueThreadSidebarSearch(query: string) {
  return (dispatch: AppDispatch) => {
    dispatch(setThreadSidebarSearch(query));
    if (sidebarSearchTimer) clearTimeout(sidebarSearchTimer);
    sidebarSearchTimer = setTimeout(() => {
      sidebarSearchTimer = null;
      void dispatch(fetchThreadsList());
    }, 300);
  };
}

export const fetchThread = createAsyncThunk(
  "threads/fetchThread",
  async (
    {
      threadId,
      mode,
      targetBlockId,
    }: { threadId: string; mode: "hard" | "soft"; targetBlockId?: string },
    { dispatch, getState },
  ) => {
    if (mode === "hard") {
      dispatch(setDetailLoading(true));
    }
    try {
      // Fetch thread metadata (without messages - we'll use blocks instead)
      const data = await trpc.threads.get.query({
        threadId,
        includeMessages: false,
      });

      if (!data) {
        if (mode === "hard") {
          dispatch(setCurrent(null));
          dispatch(
            setBidirectionalPagination({
              hasMoreBefore: false,
              hasMoreAfter: false,
              beforeCursor: null,
              afterCursor: null,
            }),
          );
        }
        return;
      }

      // Fetch blocks for the thread
      // If targetBlockId is provided, fetch blocks around that block (for deep linking)
      const blocksData = await trpc.threads.getBlocks.query({
        threadId,
        limit: INITIAL_MESSAGE_LIMIT,
        includeNested: true,
        aroundBlockId: targetBlockId,
      });

      const mapped = mapGetToThreadDetail(data);

      // Add blocks to the mapped data (no messages fallback)
      mapped.blocks = (blocksData?.blocks ?? []) as ThreadBlock[];
      mapped.messages = []; // Clear messages since we're using blocks only

      const prev = (getState() as RootState).threads.current;
      if (prev?.id === threadId) {
        mapped.participants = resolveParticipantsAfterServerMerge(
          prev.participants,
          mapped.participants,
        );
      }

      if (mode === "hard") {
        dispatch(setCurrent(mapped));
        dispatch(
          setBidirectionalPagination({
            hasMoreBefore:
              blocksData?.hasMoreBefore ?? blocksData?.hasMore ?? false,
            hasMoreAfter: blocksData?.hasMoreAfter ?? false,
            beforeCursor:
              blocksData?.beforeCursor ?? blocksData?.nextCursor ?? null,
            afterCursor: blocksData?.afterCursor ?? null,
          }),
        );
      } else if (prev?.id === threadId) {
        dispatch(mergeCurrentFromServer(mapped));
      }

      // Initialize agent state if there's an active agent run
      // Only do this on hard fetch OR if there's no existing agent state that was just cleared
      // (to avoid race condition with completed events)
      if (data.activeAgentRun) {
        const currentState = (getState() as RootState).threads.agentState[
          threadId
        ];
        // Only initialize if:
        // 1. Hard fetch (initial page load)
        // 2. No existing state, OR existing state is still processing
        // This prevents overwriting a just-cleared state from a completed event
        const shouldInitialize =
          mode === "hard" || !currentState || currentState.isProcessing;

        if (shouldInitialize) {
          dispatch(
            setAgentProcessing({
              threadId,
              agentRunId: data.activeAgentRun.agentRunId,
              agentId: data.activeAgentRun.agentId ?? "",
              agentName: data.activeAgentRun.agentName ?? undefined,
              agentKind: data.activeAgentRun.agentKind ?? null,
              isProcessing: true,
            }),
          );
        }
      }
    } catch (e) {
      console.error("Failed to fetch thread:", e);
    } finally {
      if (mode === "hard") {
        dispatch(setDetailLoading(false));
      }
    }
  },
);

export const fetchOlderMessages = createAsyncThunk(
  "threads/fetchOlderMessages",
  async (
    { threadId, cursor }: { threadId: string; cursor: string },
    { dispatch, getState },
  ) => {
    const state = getState() as RootState;
    if (state.threads.loadingOlderMessages) {
      return;
    }

    dispatch(setLoadingOlderMessages(true));

    try {
      // Use getBlocks with direction "before" to load older blocks
      const result = await trpc.threads.getBlocks.query({
        threadId,
        cursor,
        limit: MESSAGE_PAGE_SIZE,
        direction: "before",
        includeNested: true,
      });

      if (!result) {
        dispatch(setLoadingOlderMessages(false));
        return;
      }

      dispatch(
        prependBlocks({
          blocks: result.blocks as ThreadBlock[],
          hasMore: result.hasMore,
          nextCursor: result.nextCursor ?? null,
        }),
      );
    } catch (e) {
      console.error("Failed to fetch older blocks:", e);
      dispatch(setLoadingOlderMessages(false));
    }
  },
);

export const fetchNewerMessages = createAsyncThunk(
  "threads/fetchNewerMessages",
  async (
    { threadId, cursor }: { threadId: string; cursor: string },
    { dispatch, getState },
  ) => {
    const state = getState() as RootState;
    if (state.threads.loadingNewerMessages) {
      return;
    }

    dispatch(setLoadingNewerMessages(true));

    try {
      // Use getBlocks with direction "after" to load newer blocks
      const result = await trpc.threads.getBlocks.query({
        threadId,
        cursor,
        limit: MESSAGE_PAGE_SIZE,
        direction: "after",
        includeNested: true,
      });

      if (!result) {
        dispatch(setLoadingNewerMessages(false));
        return;
      }

      dispatch(
        appendBlocks({
          blocks: result.blocks as ThreadBlock[],
          hasMore: result.hasMore,
          nextCursor: result.nextCursor ?? null,
        }),
      );
    } catch (e) {
      console.error("Failed to fetch newer blocks:", e);
      dispatch(setLoadingNewerMessages(false));
    }
  },
);

export const loadSenderSnapshot = createAsyncThunk(
  "threads/loadSenderSnapshot",
  async (_, { dispatch }) => {
    try {
      const snap = await trpc.threads.currentSenderSnapshot.query();
      dispatch(senderSnapshotSet(snap));
    } catch {
      dispatch(
        senderSnapshotSet({
          memberId: "",
          displayName: "You",
          avatarUrl: null,
        }),
      );
    }
  },
);

export const streamThreadTitle = createAsyncThunk(
  "threads/streamThreadTitle",
  async (threadId: string, { dispatch, getState }) => {
    const state = getState() as RootState;
    const current = state.threads.current;
    const listRow = state.threads.items.find((item) => item.id === threadId);
    const existingTitle =
      (current?.id === threadId ? current.title : null) ??
      listRow?.title ??
      null;
    if (existingTitle?.trim()) {
      return;
    }

    dispatch(beginTitleStream(threadId));
    const requestId = (getState() as RootState).threads.titleStreamRequestId;
    let lastStreamedTitle = "";

    await consumeThreadTitleStream(
      threadId,
      (partial) => {
        lastStreamedTitle = partial;
        dispatch(titleStreamDelta({ text: partial, requestId }));
      },
      (result) => {
        dispatch(endTitleStream(requestId));
        if (
          (getState() as RootState).threads.titleStreamRequestId !== requestId
        ) {
          return;
        }

        const resolvedTitle =
          (result.title?.trim() ? result.title : "") ||
          lastStreamedTitle.trim();

        // Skip/empty: title enrichment had nothing to do — avoid refetch storms.
        if (result.skipped || !resolvedTitle) {
          return;
        }

        const listIconPatch =
          typeof result.listIcon === "string" ? result.listIcon : undefined;

        dispatch(
          applyEnrichedTitle({
            threadId,
            title: resolvedTitle,
            listIcon: listIconPatch,
            requestId,
          }),
        );
      },
    );
  },
);

export const ensureThreadForRoute = createAsyncThunk(
  "threads/ensureThreadForRoute",
  async (
    {
      selectedThreadId,
      isDraftRoute,
    }: { selectedThreadId: string | null; isDraftRoute: boolean },
    { dispatch, getState },
  ) => {
    if (!selectedThreadId || isDraftRoute) {
      dispatch(clearCurrentThread());
      return;
    }
    const current = (getState() as RootState).threads.current;
    if (current?.id === selectedThreadId) {
      await dispatch(fetchThread({ threadId: selectedThreadId, mode: "soft" }));
    } else {
      await dispatch(fetchThread({ threadId: selectedThreadId, mode: "hard" }));
    }
  },
);

export type AfterOptimisticCreate = (args: { threadId: string }) => void;

export const sendThreadMessage = createAsyncThunk<
  { threadId?: string; existingThread?: boolean; pendingMessage?: string },
  {
    selectedThreadId: string;
    content: string;
    allowEmptyMessage?: boolean;
    afterOptimisticCreate?: AfterOptimisticCreate;
    // Routing fields for external threads
    communicationChannelId?: string;
    routingAddress?: string;
    routingContactId?: string;
    routingInstanceId?: string;
    // Agent ID for thread assignment
    agentId?: string;
    // Agent version for sandbox testing
    agentVersionId?: string;
    // Workflow ID for thread assignment
    workflowId?: string;
    // Member IDs to add as participants (internal threads)
    memberIds?: string[];
  },
  { state: RootState }
>(
  "threads/sendThreadMessage",
  async (
    {
      selectedThreadId,
      content,
      allowEmptyMessage,
      afterOptimisticCreate,
      communicationChannelId,
      routingAddress,
      routingContactId,
      routingInstanceId,
      agentId,
      agentVersionId,
      workflowId,
      memberIds,
    },
    { dispatch, getState },
  ) => {
    const trimmed = content.trim();
    // Allow empty content if workflowId is present or explicitly allowed (wildcard feed)
    if (!trimmed && !workflowId && !allowEmptyMessage)
      return { threadId: undefined };

    const snap = getState().threads.senderSnapshot ?? {
      memberId: "",
      displayName: "You",
      avatarUrl: null as string | null,
    };

    // Determine thread kind based on routing
    const isExternal = !!communicationChannelId && !!routingAddress;
    const threadKind = isExternal ? "CONVERSATION" : "INTERNAL";

    dispatch(setSendingMessage(true));
    let pendingMessageId: string | null = null;

    try {
      if (selectedThreadId === NEW_THREAD_SLUG) {
        const messageId = generateDBId("threadMessage");
        pendingMessageId = messageId;

        const newThread = await trpc.threads.create.mutate({
          title: null,
          kind: threadKind,
          communicationChannelId: communicationChannelId || undefined,
          routingAddress: routingAddress || undefined,
          routingContactId: routingContactId || undefined,
          routingInstanceId: routingInstanceId || undefined,
          // Pass agent ID for thread assignment
          agentId: agentId || undefined,
          // Pass workflow ID for thread assignment
          workflowId: workflowId || undefined,
          // Pass member IDs for internal thread participants
          memberIds: memberIds && memberIds.length > 0 ? memberIds : undefined,
          // Don't send initial message - we'll handle it based on created flag
          initialMessages: undefined,
        });

        // If thread already exists, navigate to it with message pre-loaded
        if (!newThread.created) {
          // Store the pending message in Redux so it survives navigation
          dispatch(
            setPendingMessageDraft({
              threadId: newThread.id,
              content: trimmed,
            }),
          );

          // Fetch the existing thread to populate the UI
          void dispatch(fetchThread({ threadId: newThread.id, mode: "hard" }));

          // DON'T call afterOptimisticCreate here - we'll handle navigation
          // in the page component after checking the result

          return {
            threadId: newThread.id,
            existingThread: true,
            pendingMessage: trimmed,
          };
        }

        // Thread was newly created - hydrate Redux before navigate so the
        // page HARD/SOFT fetch cannot land on an empty current and wipe.
        dispatch(primeTitleStreamUi(newThread.id));

        const optimistic: ThreadDetail = {
          id: newThread.id,
          title: null,
          status: "OPEN",
          kind: threadKind,
          lastMessageAt: new Date().toISOString(),
          lastMessagePreview: trimmed,
          unreadCount: 0,
          listIcon: null,
          participants: newThread.participants?.map((p) => ({
            id: p.id,
            displayName: p.displayName,
            avatarUrl: p.avatarUrl,
            kind: p.kind as "CONTACT" | "MEMBER" | "AGENT" | "SYSTEM",
            role: p.role as "OWNER" | "PARTICIPANT" | "OBSERVER",
            joinedAt: p.joinedAt,
            memberId: p.memberId ?? null,
            agentId: p.agentId ?? null,
            contactId: p.contactId ?? null,
            workflowId: p.workflowId ?? null,
          })) ?? [
            {
              id: snap.displayName,
              displayName: snap.displayName,
              avatarUrl: snap.avatarUrl,
              kind: "MEMBER",
              role: "OWNER",
              joinedAt: new Date().toISOString(),
            },
          ],
          parentId: null,
          createdAt: newThread.createdAt,
          messages: trimmed
            ? [
                {
                  id: messageId,
                  listStableKey: messageId,
                  content: trimmed,
                  contentType: "TEXT",
                  createdAt: new Date().toISOString(),
                  sender: {
                    id: "pending-sender",
                    displayName: snap.displayName,
                    avatarUrl: snap.avatarUrl,
                    kind: "MEMBER" as const,
                  },
                  aiGenerated: false,
                  attachments: [],
                  outboundStatus: "sending",
                },
              ]
            : [],
          blocks: [],
        };

        dispatch(setCurrent(optimistic));
        afterOptimisticCreate?.({ threadId: newThread.id });

        // Send the message for new threads
        if (trimmed) {
          const sendResult = await trpc.threads.sendMessage.mutate({
            threadId: newThread.id,
            content: trimmed,
            messageId,
          });
          if (sendResult.participants?.length) {
            dispatch(
              mergeCurrentParticipants({
                threadId: newThread.id,
                participants: mapPersistedSendParticipants(
                  sendResult.participants,
                ),
              }),
            );
          }
        }

        // Don't manually replace - let fetchThread handle the merge
        const row: ThreadListRow = {
          id: newThread.id,
          title: null,
          status: "OPEN",
          kind: threadKind,
          lastMessageAt: trimmed
            ? new Date().toISOString()
            : newThread.createdAt,
          lastMessagePreview: trimmed || null,
          unreadCount: 0,
          listIcon: null,
          participants: optimistic.participants.map((participant) => ({
            id: participant.id,
            displayName: participant.displayName,
            avatarUrl: participant.avatarUrl,
            kind: participant.kind as ThreadListRow["participants"][number]["kind"],
            role: participant.role as ThreadListRow["participants"][number]["role"],
            joinedAt: participant.joinedAt,
            memberId: participant.memberId ?? null,
            contactId: participant.contactId ?? null,
            agentId: participant.agentId ?? null,
            workflowId: participant.workflowId ?? null,
          })),
          parentId: null,
          subThreadCount: 0,
        };
        dispatch(prependListRow(row));

        void dispatch(fetchThreadsListSoft());
        void dispatch(fetchThread({ threadId: newThread.id, mode: "soft" }));
        void dispatch(streamThreadTitle(newThread.id));

        return { threadId: newThread.id, existingThread: false };
      }

      const current = getState().threads.current;
      if (
        current &&
        current.id === selectedThreadId &&
        !current.title?.trim()
      ) {
        dispatch(primeTitleStreamUi(selectedThreadId));
      }

      const messageId = generateDBId("threadMessage");
      pendingMessageId = messageId;
      const createdAt = new Date().toISOString();

      const cur = getState().threads.current;
      if (cur && cur.id === selectedThreadId) {
        const memberParticipant = cur.participants.find(
          (p) => p.memberId === snap.memberId,
        );
        const senderParticipant = memberParticipant
          ? {
              id: memberParticipant.id,
              displayName: memberParticipant.displayName,
              avatarUrl: memberParticipant.avatarUrl,
              kind: memberParticipant.kind as "MEMBER",
            }
          : {
              id: "pending-sender",
              displayName: snap.displayName,
              avatarUrl: snap.avatarUrl,
              kind: "MEMBER" as const,
            };
        const lastSequence = (cur.blocks ?? []).reduce(
          (max, block) => Math.max(max, block.sequence),
          0,
        );

        dispatch(
          appendOptimisticBlock({
            threadId: selectedThreadId,
            messageId,
            block: {
              id: `optimistic-${messageId}`,
              threadId: selectedThreadId,
              kind: "MESSAGE",
              sequence: lastSequence + 1,
              status: null,
              content: trimmed,
              contentStructured: null,
              createdAt,
              updatedAt: createdAt,
              parentBlockId: null,
              participant: senderParticipant,
              message: {
                id: messageId,
                content: trimmed,
                contentRaw: null,
                contentType: "TEXT",
                aiGenerated: false,
                sender: senderParticipant,
                attachments: [],
                sendStatus: "SENDING",
              },
              agentRun: null,
              workflowRun: null,
              subThread: null,
              threadLink: null,
              childBlocks: [],
            },
          }),
        );

        dispatch(
          updateThreadActivity({
            threadId: selectedThreadId,
            lastMessageAt: createdAt,
            lastMessagePreview: trimmed,
            lastActivityAt: createdAt,
            lastActivityType: "message",
          }),
        );

        // Unlock composer immediately — message is already visible optimistically.
        dispatch(setSendingMessage(false));
      }

      const sent = await trpc.threads.sendMessage.mutate({
        threadId: selectedThreadId,
        content: trimmed,
        messageId,
      });

      if (sent.participants?.length) {
        dispatch(
          mergeCurrentParticipants({
            threadId: selectedThreadId,
            participants: mapPersistedSendParticipants(sent.participants),
          }),
        );
      }

      dispatch(
        confirmOptimisticMessage({
          threadId: selectedThreadId,
          messageId: sent.id,
          createdAt: sent.createdAt,
          sender: sent.sender,
          content: sent.content,
          contentType: sent.contentType,
          aiGenerated: sent.aiGenerated,
        }),
      );

      dispatch(
        updateThreadActivity({
          threadId: selectedThreadId,
          lastMessageAt: sent.createdAt,
          lastMessagePreview: sent.content,
          lastActivityAt: sent.createdAt,
          lastActivityType: "message",
        }),
      );

      const titled = getState().threads.current;
      if (titled?.id === selectedThreadId && !titled.title?.trim()) {
        void dispatch(streamThreadTitle(selectedThreadId));
      }

      void dispatch(fetchThread({ threadId: selectedThreadId, mode: "soft" }));

      return { threadId: selectedThreadId };
    } catch (e) {
      console.error("Failed to send message:", e);
      const failedId = pendingMessageId;
      if (failedId) {
        dispatch(markMessageOutboundFailed({ messageId: failedId }));
      }
      throw e;
    } finally {
      dispatch(setSendingMessage(false));
    }
  },
);

export const resolveThread = createAsyncThunk(
  "threads/resolveThread",
  async (
    {
      threadId,
      selectedThreadId,
    }: { threadId: string; selectedThreadId: string | null },
    { dispatch },
  ) => {
    await trpc.threads.resolve.mutate({ threadId });
    await dispatch(fetchThreadsListSoft());
    if (selectedThreadId === threadId) {
      await dispatch(fetchThread({ threadId, mode: "hard" }));
    }
  },
);

export const archiveThread = createAsyncThunk(
  "threads/archiveThread",
  async (threadId: string, { dispatch }) => {
    await trpc.threads.archive.mutate({ threadId });
    await dispatch(fetchThreadsListSoft());
  },
);

export const reopenThread = createAsyncThunk(
  "threads/reopenThread",
  async (threadId: string, { dispatch }) => {
    await trpc.threads.reopen.mutate({ threadId });
    await dispatch(fetchThreadsListSoft());
  },
);

export const updateThread = createAsyncThunk(
  "threads/updateThread",
  async (
    { threadId, title }: { threadId: string; title: string },
    { dispatch, getState },
  ) => {
    dispatch(patchThreadListMeta({ threadId, title }));

    try {
      const result = await trpc.threads.update.mutate({ threadId, title });

      const state = getState() as RootState;
      const currentThread = state.threads.current;

      if (currentThread?.id === threadId) {
        await dispatch(fetchThread({ threadId, mode: "soft" }));
      }

      return result;
    } catch (error) {
      await dispatch(fetchThreadsListSoft());
      throw error;
    }
  },
);

export const toggleThreadPin = createAsyncThunk(
  "threads/toggleThreadPin",
  async (
    { threadId, pinned }: { threadId: string; pinned: boolean },
    { dispatch },
  ) => {
    const result = await trpc.threads.setThreadPin.mutate({ threadId, pinned });
    dispatch(
      setThreadPinned({
        threadId: result.id,
        pinnedAt: result.pinnedAt,
      }),
    );
    return result;
  },
);

export const stopAgentRun = createAsyncThunk(
  "threads/stopAgentRun",
  async (
    { threadId, agentRunId }: { threadId: string; agentRunId: string },
    { dispatch },
  ) => {
    try {
      const response = await fetch("/api/threads/stop-agent", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ threadId, agentRunId }),
      });

      if (!response.ok) {
        throw new Error("Failed to stop agent");
      }

      dispatch(clearAgentState({ threadId }));
      await dispatch(fetchThread({ threadId, mode: "soft" }));
    } catch (error) {
      console.error("Failed to stop agent:", error);
      throw error;
    }
  },
);

export const selectThreadsItems = (s: RootState) => s.threads.items;
export const selectThreadsListLoading = (s: RootState) => s.threads.listLoading;
export const selectThreadCurrent = (s: RootState) => s.threads.current;
export const selectThreadDetailLoading = (s: RootState) =>
  s.threads.detailLoading;
export const selectThreadSendingMessage = (s: RootState) =>
  s.threads.sendingMessage;
export const selectTitleStream = createSelector(
  [
    (s: RootState) => s.threads.titleStreamActive,
    (s: RootState) => s.threads.titleStreamText,
    (s: RootState) => s.threads.titleStreamThreadId,
  ],
  (active, text, threadId) => ({ active, text, threadId }),
);
export const selectSenderSnapshot = (s: RootState) => s.threads.senderSnapshot;
export const selectAgentState = (threadId: string) => (s: RootState) =>
  s.threads.agentState[threadId] ?? null;
// New thread routing selectors
export const selectNewThreadType = (s: RootState) => s.threads.newThreadType;
export const selectSelectedChannelId = (s: RootState) =>
  s.threads.selectedChannelId;
export const selectRoutingAddress = (s: RootState) => s.threads.routingAddress;
// Message pagination selectors (bidirectional)
export const selectHasMoreBefore = (s: RootState) => s.threads.hasMoreBefore;
export const selectHasMoreAfter = (s: RootState) => s.threads.hasMoreAfter;
export const selectBeforeCursor = (s: RootState) => s.threads.beforeCursor;
export const selectAfterCursor = (s: RootState) => s.threads.afterCursor;
export const selectLoadingOlderMessages = (s: RootState) =>
  s.threads.loadingOlderMessages;
export const selectLoadingNewerMessages = (s: RootState) =>
  s.threads.loadingNewerMessages;

// Stable list order selectors
export const selectListLoadedAt = (s: RootState) => s.threads.listLoadedAt;
export const selectPendingNewThreads = (s: RootState) =>
  s.threads.pendingNewThreads;
export const selectPendingNewThreadCount = (s: RootState) =>
  s.threads.pendingNewThreads.length;
export const selectListHasMore = (s: RootState) => s.threads.listHasMore;
export const selectListLoadingMore = (s: RootState) =>
  s.threads.listLoadingMore;

// Thread filter types for sub-navigation
export type ThreadFilterType = "inbox" | "conversations";

// Selector to filter threads by category
// Uses the same logic as the threads page for consistency
export const selectFilteredThreads =
  (filter: ThreadFilterType | null, currentMemberId: string | null) =>
  (s: RootState): ThreadListRow[] => {
    const items = s.threads.items;

    if (!filter) return items.filter((t) => t.kind !== "TESTING");

    switch (filter) {
      case "inbox":
        return items.filter(
          (thread) =>
            thread.kind !== "TESTING" &&
            isThreadInInbox(thread, currentMemberId),
        );
      case "conversations":
        return items.filter(
          (thread) =>
            thread.kind !== "TESTING" && isThreadInConversations(thread),
        );
      default:
        return items.filter((t) => t.kind !== "TESTING");
    }
  };

// Selector to compute thread counts by category
// Uses the same logic as the threads page for consistency
export const selectThreadCounts =
  (currentMemberId: string | null) =>
  (s: RootState): Record<ThreadFilterType, number> & { total: number } => {
    const items = s.threads.items;

    let inboxCount = 0;
    let conversationsCount = 0;

    for (const thread of items) {
      if (thread.kind === "TESTING") continue;

      if (isThreadInInbox(thread, currentMemberId)) inboxCount++;
      if (isThreadInConversations(thread)) conversationsCount++;
    }

    return {
      inbox: inboxCount,
      conversations: conversationsCount,
      total: items.filter((t) => t.kind !== "TESTING").length,
    };
  };

export default threadsSlice.reducer;
